import {
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Box,
  Chip,
  Stack,
} from "@mui/material";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";

export function BloqueProceso({ titulo, info, diasRestantes }) {
  if (!info) return null;

  // ─── ILIMITADO ───────────────────────────────────────────────────────────────
  if (info.ilimitado || info.permitido === 0) {
    return (
      <Card
        elevation={3}
        sx={{
          borderRadius: 3,
          height: "100%",
          background: "linear-gradient(135deg, #e3f2fd, #ffffff)",
        }}
      >
        <CardContent>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={600}>
                {titulo}
              </Typography>
              <Chip label="Ilimitado" color="success" size="small" />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AllInclusiveIcon color="primary" sx={{ fontSize: 36 }} />
              <Typography variant="body2" color="text.secondary">
                Sin restricciones de uso
              </Typography>
            </Stack>
            {diasRestantes !== undefined && (
              <Typography variant="caption" color="text.secondary">
                {diasRestantes} días restantes del mes
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // ─── CÁLCULOS ────────────────────────────────────────────────────────────────
  const porcentaje =
    info.permitido > 0 ? Math.min(100, (info.usado / info.permitido) * 100) : 0;
  const restante = info.restante;

  let color = "primary";
  let estadoTexto = "Disponible";
  let badgeColor = "success";
  let bgGradient = "linear-gradient(135deg, #f1f8e9, #ffffff)";

  if (porcentaje >= 90 || restante <= 5) {
    color = "error";
    badgeColor = "error";
    estadoTexto = "Crítico";
    bgGradient = "linear-gradient(135deg, #ffebee, #ffffff)";
  } else if (porcentaje >= 70 || restante <= 10) {
    color = "warning";
    badgeColor = "warning";
    estadoTexto = "Por agotarse";
    bgGradient = "linear-gradient(135deg, #fff8e1, #ffffff)";
  }

  // Proyección: si quedan pocos días, ¿alcanza?
  let proyeccionTexto = null;
  if (diasRestantes !== undefined && diasRestantes > 0 && restante > 0 && info.usado > 0) {
    const diasTranscurridos = new Date().getDate();
    const consumoDiario = info.usado / diasTranscurridos;
    const proyectado = Math.round(consumoDiario * diasRestantes);
    if (proyectado > restante) {
      proyeccionTexto = `⚠ Proyección: ${proyectado} más (límite superado)`;
    }
  }

  return (
    <Card
      elevation={3}
      sx={{
        borderRadius: 3,
        height: "100%",
        background: bgGradient,
        transition: "0.2s",
        "&:hover": { transform: "scale(1.02)" },
      }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          {/* Titulo + Estado */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              {titulo}
            </Typography>
            <Chip label={estadoTexto} color={badgeColor} size="small" />
          </Stack>

          {/* Porcentaje grande */}
          <Typography variant="h3" fontWeight={800} color={color}>
            {Math.round(porcentaje)}%
          </Typography>

          {/* Barra */}
          <Box>
            <LinearProgress
              variant="determinate"
              value={porcentaje}
              color={color}
              sx={{ height: 10, borderRadius: 5 }}
            />
          </Box>

          {/* Usado / Restante */}
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Usado: <b>{info.usado}</b> de <b>{info.permitido}</b>
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              color={restante <= 5 ? "error.main" : restante <= 10 ? "warning.main" : "text.primary"}
            >
              {restante} restantes
            </Typography>
          </Stack>

          {/* Días del mes */}
          {diasRestantes !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {diasRestantes} días restantes del mes
            </Typography>
          )}

          {/* Proyección */}
          {proyeccionTexto && (
            <Typography variant="caption" color="error.main" fontWeight={600}>
              {proyeccionTexto}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
